// GENERADO por scripts/assets/derive.py — no editar a mano.
// Dueño: FORGE-BRAVO (dominio `assets`). Regenerar con:  python3 scripts/assets/derive.py
//
// Contrato de assets de la gira 2027. Las páginas consumen SIEMPRE este manifiesto:
// nunca una ruta a pelo. Cada entrada trae sus derivadas AVIF/WebP/fallback con anchuras
// reales para `srcset`, su `alt` en español, su proporción y su procedencia.
//
// Uso:
//   import { assets } from '@/assets/manifest';
//   const a = assets['hub/hero-cielo-galaxia'];
//   <picture>
//     {a.sources.map((s) => <source key={s.format} type={s.type} srcSet={s.srcSet} sizes="100vw" />)}
//     <img
//       src={a.src}
//       srcSet={a.fallbackSrcSet}   {/* sin esto, un navegador viejo se traga la derivada mayor */}
//       sizes="100vw"
//       alt={a.alt}
//       width={a.width}
//       height={a.height}
//       loading="lazy"
//       decoding="async"
//     />
//   </picture>

/** De dónde sale la imagen: material real del cliente o generada con gpt-image-2 (ADR-004). */
export type AssetOrigen = 'real' | 'generado';

/**
 * QUÉ CARA ES. Solo lo llevan los assets en los que sale Azrahel, y hay que mirarlo ANTES de
 * cablear un retrato en una sección.
 *
 * - `'marca-ia'`  — retrato que la marca generó con IA a partir de una foto real. Es la pose
 *                   que calcan los 6 mockups, pero **la cara no es exactamente la del cliente**.
 * - `'foto-real'` — su cara, editada a partir de una fotografía de cámara suya y **medida**
 *                   contra ella (ver abajo).
 *
 * **Las dos familias no se mezclan en la misma página.** Puestas juntas se leen como dos
 * personas distintas.
 *
 * DECIDIDO POR EL ORACLE (25-jul-2026): el sitio usaba `'marca-ia'` en todo lo visible.
 *
 * **CORREGIDO EL 30-jul-2026 POR QUEJA DEL PROPIO AZRAHEL, y tenía razón con números.** Dijo que
 * la IA le cambiaba la cara. Medido con embeddings ArcFace (`buffalo_l`) contra una foto de
 * cámara suya, el suelo de «es él» son **0,62** —lo que dan entre sí dos fotos reales suyas— y
 * lo que había publicado daba **0,519 el recorte, 0,510 el retrato y 0,533 el círculo**: a
 * medio camino entre él y la cara vieja de marca (0,384). No era impresión suya.
 *
 * Los tres se rehacen desde `portraits-hires/azrahel-2025-03-15-frontal.jpg` (cámara, sin
 * retocar) editando **solo ropa y fondo** con `gpt-image-2` por `/v1/images/edits`, que parte de
 * la foto en vez de generar una cara. Resultado servido: **0,890 a 0,927**, por encima del 0,887
 * que dan entre sí dos fotos suyas distintas del mismo día. Es su cara.
 *
 * **NO SE ACEPTA UN RETRATO SIN MEDIRLO.** Un render que «se parece» es exactamente el fallo que
 * costó esta vuelta: se parecía lo bastante para pasar tres auditorías a ojo y lo bastante poco
 * para que el cliente lo viera a la primera. El número es el gate.
 */
export type AssetRegistro = 'marca-ia' | 'foto-real';

export type AssetFormato = 'avif' | 'webp' | 'jpg' | 'png';

/** Una fuente de `<picture>`: un formato con todas sus anchuras. */
export interface AssetSource {
  readonly format: AssetFormato;
  /** Valor listo para el atributo `type` de `<source>`. */
  readonly type: string;
  /** Valor listo para `srcSet`: "url 640w, url 960w, ...". */
  readonly srcSet: string;
}

export interface Asset {
  readonly key: string;
  /** Texto alternativo en español, descriptivo y útil. Nunca vacío. */
  readonly alt: string;
  readonly origen: AssetOrigen;
  /**
   * Qué cara es. Presente SOLO en los assets donde sale Azrahel; `undefined` en el resto
   * (paisajes, marca, ornamentos — por eso el set de Cancún no lo lleva: no sale nadie).
   * Ver `AssetRegistro`: el sitio usa `'marca-ia'`, decidido por el ORACLE.
   */
  readonly registro?: AssetRegistro;
  /** Sección del mockup a la que sirve. */
  readonly seccion: string;
  /** Ancho intrínseco de la derivada mayor (la que apunta `src`). */
  readonly width: number;
  /** Alto intrínseco de la derivada mayor. */
  readonly height: number;
  /** width / height, para reservar hueco y evitar saltos de layout. */
  readonly aspectRatio: number;
  /** Tiene canal alfa real: no se le puede poner un fondo opaco detrás sin pensarlo. */
  readonly transparente: boolean;
  /** URL del fallback mayor, para el `src` del `<img>`. */
  readonly src: string;
  /** Formato del fallback: JPEG si la imagen es opaca, PNG si tiene alfa. */
  readonly fallbackFormat: AssetFormato;
  /**
   * `srcSet` del fallback, para el propio `<img>`. Va SIEMPRE: sin él, un navegador sin
   * AVIF ni WebP se descarga la derivada mayor en un móvil de 320 px.
   */
  readonly fallbackSrcSet: string;
  /** AVIF primero, luego WebP. El fallback va en `src` + `fallbackSrcSet`. */
  readonly sources: readonly AssetSource[];
  /** Anchuras realmente disponibles en disco. */
  readonly widths: readonly number[];
}

const MIME: Record<AssetFormato, string> = {
  avif: 'image/avif',
  webp: 'image/webp',
  jpg: 'image/jpeg',
  png: 'image/png',
};

export const assets = {
  'azrahel/recorte': {
    key: 'azrahel/recorte',
    alt: 'Azrahel del Mayor con traje oscuro y camisa blanca, recortado sobre el fondo',
    origen: 'real',
    registro: 'foto-real',
    seccion: 'Las 6 páginas · retrato de Azrahel recortado sobre el hero',
    width: 672,
    height: 804,
    aspectRatio: 0.8358,
    transparente: true,
    src: '/media/azrahel/recorte-672.png?v=91e103c3',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/azrahel/recorte-420.png?v=6dc34431 420w, /media/azrahel/recorte-640.png?v=161b4cde 640w, /media/azrahel/recorte-672.png?v=91e103c3 672w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/azrahel/recorte-420.avif?v=a4430438 420w, /media/azrahel/recorte-640.avif?v=8364685f 640w, /media/azrahel/recorte-672.avif?v=3fe96b02 672w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/azrahel/recorte-420.webp?v=e73411e9 420w, /media/azrahel/recorte-640.webp?v=f71b1088 640w, /media/azrahel/recorte-672.webp?v=db7498d2 672w' },
    ],
    widths: [420, 640, 672],
  },
  'azrahel/circulo': {
    key: 'azrahel/circulo',
    alt: 'Retrato circular de Azrahel del Mayor con traje oscuro y camisa blanca sobre fondo crema',
    origen: 'real',
    registro: 'foto-real',
    seccion: 'Bogotá, México, Buenos Aires · bloque \'Azrahel\' (retrato circular)',
    width: 512,
    height: 512,
    aspectRatio: 1.0,
    transparente: true,
    src: '/media/azrahel/circulo-512.png?v=32472910',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/azrahel/circulo-192.png?v=742e716b 192w, /media/azrahel/circulo-288.png?v=904e71ae 288w, /media/azrahel/circulo-384.png?v=1f6ee6db 384w, /media/azrahel/circulo-512.png?v=32472910 512w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/azrahel/circulo-192.avif?v=3c21be30 192w, /media/azrahel/circulo-288.avif?v=0bb0e8d3 288w, /media/azrahel/circulo-384.avif?v=82564525 384w, /media/azrahel/circulo-512.avif?v=35b1cc1b 512w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/azrahel/circulo-192.webp?v=59522362 192w, /media/azrahel/circulo-288.webp?v=da620c76 288w, /media/azrahel/circulo-384.webp?v=c810812f 384w, /media/azrahel/circulo-512.webp?v=a4b4a0da 512w' },
    ],
    widths: [192, 288, 384, 512],
  },
  'azrahel/retrato': {
    key: 'azrahel/retrato',
    alt: 'Azrahel del Mayor con traje oscuro y camisa blanca, retrato editorial',
    origen: 'real',
    registro: 'foto-real',
    seccion: 'Hub y Miami · bloque \'Azrahel del Mayor\' (retrato editorial 4:5)',
    width: 700,
    height: 875,
    aspectRatio: 0.8,
    transparente: false,
    src: '/media/azrahel/retrato-700.jpg?v=30b656ad',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/azrahel/retrato-420.jpg?v=6f714950 420w, /media/azrahel/retrato-640.jpg?v=97474169 640w, /media/azrahel/retrato-700.jpg?v=30b656ad 700w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/azrahel/retrato-420.avif?v=69b67338 420w, /media/azrahel/retrato-640.avif?v=b2f60752 640w, /media/azrahel/retrato-700.avif?v=a6fc2e6a 700w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/azrahel/retrato-420.webp?v=a2b2fb07 420w, /media/azrahel/retrato-640.webp?v=2343650a 640w, /media/azrahel/retrato-700.webp?v=29f9d26e 700w' },
    ],
    widths: [420, 640, 700],
  },
  'azrahel/estudio-medio': {
    key: 'azrahel/estudio-medio',
    alt: 'Azrahel del Mayor de frente, chaqueta azul marino sobre camisa blanca, retrato de estudio',
    origen: 'real',
    registro: 'foto-real',
    seccion: 'Retrato de estudio · plano medio (bloque \'Azrahel del Mayor\')',
    width: 1600,
    height: 1964,
    aspectRatio: 0.8147,
    transparente: false,
    src: '/media/azrahel/estudio-medio-1600.jpg?v=f7230e56',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/azrahel/estudio-medio-420.jpg?v=0b16d17b 420w, /media/azrahel/estudio-medio-640.jpg?v=54aecebf 640w, /media/azrahel/estudio-medio-900.jpg?v=d7f2a4bd 900w, /media/azrahel/estudio-medio-1200.jpg?v=a5290c5c 1200w, /media/azrahel/estudio-medio-1600.jpg?v=f7230e56 1600w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/azrahel/estudio-medio-420.avif?v=60efc715 420w, /media/azrahel/estudio-medio-640.avif?v=72c1d1d2 640w, /media/azrahel/estudio-medio-900.avif?v=006c8dbd 900w, /media/azrahel/estudio-medio-1200.avif?v=30bb7d25 1200w, /media/azrahel/estudio-medio-1600.avif?v=71717c0d 1600w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/azrahel/estudio-medio-420.webp?v=f4bbcfbb 420w, /media/azrahel/estudio-medio-640.webp?v=157104d9 640w, /media/azrahel/estudio-medio-900.webp?v=7f485356 900w, /media/azrahel/estudio-medio-1200.webp?v=e298366c 1200w, /media/azrahel/estudio-medio-1600.webp?v=fe6bd798 1600w' },
    ],
    widths: [420, 640, 900, 1200, 1600],
  },
  'azrahel/estudio-circulo': {
    key: 'azrahel/estudio-circulo',
    alt: 'Retrato circular de Azrahel del Mayor, foto de estudio sobre fondo cálido',
    origen: 'real',
    registro: 'foto-real',
    seccion: 'Retrato de estudio · círculo (bloques \'Azrahel\' de las ciudades)',
    width: 512,
    height: 512,
    aspectRatio: 1.0,
    transparente: true,
    src: '/media/azrahel/estudio-circulo-512.png?v=75698b37',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/azrahel/estudio-circulo-192.png?v=259e7fa7 192w, /media/azrahel/estudio-circulo-288.png?v=3887fe03 288w, /media/azrahel/estudio-circulo-384.png?v=de78d5fd 384w, /media/azrahel/estudio-circulo-512.png?v=75698b37 512w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/azrahel/estudio-circulo-192.avif?v=0ea37dca 192w, /media/azrahel/estudio-circulo-288.avif?v=a04dec7a 288w, /media/azrahel/estudio-circulo-384.avif?v=fd70b31b 384w, /media/azrahel/estudio-circulo-512.avif?v=e2859b67 512w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/azrahel/estudio-circulo-192.webp?v=0e5cc214 192w, /media/azrahel/estudio-circulo-288.webp?v=371b28ee 288w, /media/azrahel/estudio-circulo-384.webp?v=5030e4c1 384w, /media/azrahel/estudio-circulo-512.webp?v=5f023937 512w' },
    ],
    widths: [192, 288, 384, 512],
  },
  'azrahel/estudio-editorial': {
    key: 'azrahel/estudio-editorial',
    alt: 'Azrahel del Mayor, retrato editorial de estudio en formato vertical',
    origen: 'real',
    registro: 'foto-real',
    seccion: 'Retrato de estudio · encuadre editorial 4:5',
    width: 1520,
    height: 1900,
    aspectRatio: 0.8,
    transparente: false,
    src: '/media/azrahel/estudio-editorial-1520.jpg?v=f122ff19',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/azrahel/estudio-editorial-420.jpg?v=c348b953 420w, /media/azrahel/estudio-editorial-640.jpg?v=c7f8fca8 640w, /media/azrahel/estudio-editorial-900.jpg?v=ea70a6ca 900w, /media/azrahel/estudio-editorial-1200.jpg?v=6b1052c9 1200w, /media/azrahel/estudio-editorial-1520.jpg?v=f122ff19 1520w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/azrahel/estudio-editorial-420.avif?v=5653be9f 420w, /media/azrahel/estudio-editorial-640.avif?v=55d804d8 640w, /media/azrahel/estudio-editorial-900.avif?v=b13bb5f1 900w, /media/azrahel/estudio-editorial-1200.avif?v=e9c56ed4 1200w, /media/azrahel/estudio-editorial-1520.avif?v=11295e1c 1520w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/azrahel/estudio-editorial-420.webp?v=f193eb12 420w, /media/azrahel/estudio-editorial-640.webp?v=e2657979 640w, /media/azrahel/estudio-editorial-900.webp?v=a6c8ffe3 900w, /media/azrahel/estudio-editorial-1200.webp?v=2d3adc2a 1200w, /media/azrahel/estudio-editorial-1520.webp?v=3dbe7805 1520w' },
    ],
    widths: [420, 640, 900, 1200, 1520],
  },
  'azrahel/on-air': {
    key: 'azrahel/on-air',
    alt: 'Azrahel del Mayor sentado en un plató de radio junto al rótulo ON AIR, en blanco y negro',
    origen: 'real',
    registro: 'foto-real',
    seccion: 'Editorial en blanco y negro · plató \'ON AIR\' (bloque de autoridad / prensa)',
    width: 2048,
    height: 1152,
    aspectRatio: 1.7778,
    transparente: false,
    src: '/media/azrahel/on-air-2048.jpg?v=06ebe417',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/azrahel/on-air-640.jpg?v=056e4494 640w, /media/azrahel/on-air-960.jpg?v=12ba35c3 960w, /media/azrahel/on-air-1280.jpg?v=1bb9ab4c 1280w, /media/azrahel/on-air-1600.jpg?v=e9be6636 1600w, /media/azrahel/on-air-2048.jpg?v=06ebe417 2048w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/azrahel/on-air-640.avif?v=5675ba5f 640w, /media/azrahel/on-air-960.avif?v=d1903254 960w, /media/azrahel/on-air-1280.avif?v=2427a74d 1280w, /media/azrahel/on-air-1600.avif?v=7e94eadf 1600w, /media/azrahel/on-air-2048.avif?v=0d999953 2048w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/azrahel/on-air-640.webp?v=de01da01 640w, /media/azrahel/on-air-960.webp?v=10ee0f5c 960w, /media/azrahel/on-air-1280.webp?v=0b3ab99d 1280w, /media/azrahel/on-air-1600.webp?v=0b5fff61 1600w, /media/azrahel/on-air-2048.webp?v=55730773 2048w' },
    ],
    widths: [640, 960, 1280, 1600, 2048],
  },
  'marca/lockup': {
    key: 'marca/lockup',
    alt: 'Logo AΩGR — Escuela Pleyadiana, espiral dorada y wordmark alado',
    origen: 'real',
    seccion: 'Todas las páginas · cabecera y footer (insignia de marca)',
    width: 540,
    height: 436,
    aspectRatio: 1.2385,
    transparente: true,
    src: '/media/marca/lockup-540.png?v=3f834951',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/marca/lockup-180.png?v=32fe8b9e 180w, /media/marca/lockup-270.png?v=af501f55 270w, /media/marca/lockup-360.png?v=96f57e33 360w, /media/marca/lockup-540.png?v=3f834951 540w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/marca/lockup-180.avif?v=4e4ed625 180w, /media/marca/lockup-270.avif?v=f0f25613 270w, /media/marca/lockup-360.avif?v=b5f528a5 360w, /media/marca/lockup-540.avif?v=188267ae 540w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/marca/lockup-180.webp?v=22020908 180w, /media/marca/lockup-270.webp?v=e2ca2faf 270w, /media/marca/lockup-360.webp?v=9b07442d 360w, /media/marca/lockup-540.webp?v=fde0a485 540w' },
    ],
    widths: [180, 270, 360, 540],
  },
  'marca/espiral': {
    key: 'marca/espiral',
    alt: 'Espiral dorada de la marca AΩGR',
    origen: 'real',
    seccion: 'Todas las páginas · sello, favicon y marca de agua',
    width: 540,
    height: 558,
    aspectRatio: 0.9677,
    transparente: true,
    src: '/media/marca/espiral-540.png?v=5e35a02a',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/marca/espiral-180.png?v=ca04020d 180w, /media/marca/espiral-270.png?v=4e592099 270w, /media/marca/espiral-360.png?v=6a5beafc 360w, /media/marca/espiral-540.png?v=5e35a02a 540w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/marca/espiral-180.avif?v=802ddb8a 180w, /media/marca/espiral-270.avif?v=fccf5444 270w, /media/marca/espiral-360.avif?v=eb705be1 360w, /media/marca/espiral-540.avif?v=4f20d1d9 540w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/marca/espiral-180.webp?v=09b22537 180w, /media/marca/espiral-270.webp?v=352f7108 270w, /media/marca/espiral-360.webp?v=b6c39db8 360w, /media/marca/espiral-540.webp?v=29674677 540w' },
    ],
    widths: [180, 270, 360, 540],
  },
  'marca/completo': {
    key: 'marca/completo',
    alt: 'Logo AΩGR completo con espiral, monograma y wordmark',
    origen: 'real',
    seccion: 'Hub y Bogotá · emblema grande de marca',
    width: 540,
    height: 695,
    aspectRatio: 0.777,
    transparente: true,
    src: '/media/marca/completo-540.png?v=9777f386',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/marca/completo-180.png?v=c5aeb202 180w, /media/marca/completo-270.png?v=09fa8a1c 270w, /media/marca/completo-360.png?v=4d69d4c3 360w, /media/marca/completo-540.png?v=9777f386 540w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/marca/completo-180.avif?v=c6eeb664 180w, /media/marca/completo-270.avif?v=3c11ea04 270w, /media/marca/completo-360.avif?v=c456bae0 360w, /media/marca/completo-540.avif?v=2c50d0a1 540w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/marca/completo-180.webp?v=c9fadd29 180w, /media/marca/completo-270.webp?v=6f74f736 270w, /media/marca/completo-360.webp?v=ea1f5457 360w, /media/marca/completo-540.webp?v=138d95d4 540w' },
    ],
    widths: [180, 270, 360, 540],
  },
  'comun/charla-miami-2023': {
    key: 'comun/charla-miami-2023',
    alt: 'Grupo de asistentes a la conferencia de Azrahel en Miami, 2023',
    origen: 'real',
    seccion: 'Disponible (prueba social real) — ninguna sección de los mockups la pide',
    width: 1280,
    height: 865,
    aspectRatio: 1.4798,
    transparente: false,
    src: '/media/comun/charla-miami-2023-1280.jpg?v=143476b9',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/comun/charla-miami-2023-480.jpg?v=96fedb0e 480w, /media/comun/charla-miami-2023-720.jpg?v=e74a92b9 720w, /media/comun/charla-miami-2023-960.jpg?v=8e12e602 960w, /media/comun/charla-miami-2023-1280.jpg?v=143476b9 1280w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/comun/charla-miami-2023-480.avif?v=2b54a98a 480w, /media/comun/charla-miami-2023-720.avif?v=f18098f4 720w, /media/comun/charla-miami-2023-960.avif?v=21cd7272 960w, /media/comun/charla-miami-2023-1280.avif?v=7a3c13da 1280w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/comun/charla-miami-2023-480.webp?v=4d933ad2 480w, /media/comun/charla-miami-2023-720.webp?v=09eabf01 720w, /media/comun/charla-miami-2023-960.webp?v=fc1e36f9 960w, /media/comun/charla-miami-2023-1280.webp?v=b5ed4a17 1280w' },
    ],
    widths: [480, 720, 960, 1280],
  },
  'hub/hero-cielo-galaxia': {
    key: 'hub/hero-cielo-galaxia',
    alt: 'Galaxia espiral luminosa sobre una bahía costera al amanecer',
    origen: 'generado',
    seccion: 'Hub · hero (fondo tras el titular y el retrato)',
    width: 2048,
    height: 1024,
    aspectRatio: 2.0,
    transparente: false,
    src: '/media/hub/hero-cielo-galaxia-2048.jpg?v=c34523bc',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/hub/hero-cielo-galaxia-640.jpg?v=a9c5bb86 640w, /media/hub/hero-cielo-galaxia-960.jpg?v=b75a3103 960w, /media/hub/hero-cielo-galaxia-1280.jpg?v=8419fdb3 1280w, /media/hub/hero-cielo-galaxia-1600.jpg?v=86b8b379 1600w, /media/hub/hero-cielo-galaxia-2048.jpg?v=c34523bc 2048w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/hub/hero-cielo-galaxia-640.avif?v=b3dc0988 640w, /media/hub/hero-cielo-galaxia-960.avif?v=c1d48a73 960w, /media/hub/hero-cielo-galaxia-1280.avif?v=6692a236 1280w, /media/hub/hero-cielo-galaxia-1600.avif?v=5ed6fa20 1600w, /media/hub/hero-cielo-galaxia-2048.avif?v=353faf96 2048w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/hub/hero-cielo-galaxia-640.webp?v=53df5aa1 640w, /media/hub/hero-cielo-galaxia-960.webp?v=b6ad4edb 960w, /media/hub/hero-cielo-galaxia-1280.webp?v=502db9bb 1280w, /media/hub/hero-cielo-galaxia-1600.webp?v=72363884 1600w, /media/hub/hero-cielo-galaxia-2048.webp?v=ffd0d766 2048w' },
    ],
    widths: [640, 960, 1280, 1600, 2048],
  },
  'hub/camara-cuantica': {
    key: 'hub/camara-cuantica',
    alt: 'Interior luminoso de la Cámara Cuántica Pleyadiana con una rosetón de geometría sagrada dorada',
    origen: 'generado',
    seccion: 'Hub · banda \'La Cámara Cuántica Pleyadiana\' (imagen a la izquierda)',
    width: 2048,
    height: 1024,
    aspectRatio: 2.0,
    transparente: false,
    src: '/media/hub/camara-cuantica-2048.jpg?v=9e30f4cb',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/hub/camara-cuantica-640.jpg?v=9b7f8e1b 640w, /media/hub/camara-cuantica-960.jpg?v=3b0c1731 960w, /media/hub/camara-cuantica-1280.jpg?v=8ff2497c 1280w, /media/hub/camara-cuantica-1600.jpg?v=0dcbada4 1600w, /media/hub/camara-cuantica-2048.jpg?v=9e30f4cb 2048w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/hub/camara-cuantica-640.avif?v=0bf4ea40 640w, /media/hub/camara-cuantica-960.avif?v=07ddaccb 960w, /media/hub/camara-cuantica-1280.avif?v=34b3a71c 1280w, /media/hub/camara-cuantica-1600.avif?v=0706d908 1600w, /media/hub/camara-cuantica-2048.avif?v=3603b73a 2048w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/hub/camara-cuantica-640.webp?v=12caf0f3 640w, /media/hub/camara-cuantica-960.webp?v=70570a73 960w, /media/hub/camara-cuantica-1280.webp?v=2a8036f9 1280w, /media/hub/camara-cuantica-1600.webp?v=6ab1658f 1600w, /media/hub/camara-cuantica-2048.webp?v=ea24bb4b 2048w' },
    ],
    widths: [640, 960, 1280, 1600, 2048],
  },
  'hub/banda-cielo': {
    key: 'hub/banda-cielo',
    alt: 'Cielo suave con nubes blancas y un resplandor cálido en el horizonte',
    origen: 'generado',
    seccion: 'Hub · banda \'Azrahel del Mayor\' (fondo suave tras el retrato y el texto)',
    width: 2048,
    height: 1024,
    aspectRatio: 2.0,
    transparente: false,
    src: '/media/hub/banda-cielo-2048.jpg?v=b881643c',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/hub/banda-cielo-640.jpg?v=a954eadb 640w, /media/hub/banda-cielo-960.jpg?v=c17cf1aa 960w, /media/hub/banda-cielo-1280.jpg?v=01a0293d 1280w, /media/hub/banda-cielo-1600.jpg?v=4e0edce9 1600w, /media/hub/banda-cielo-2048.jpg?v=b881643c 2048w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/hub/banda-cielo-640.avif?v=3d44b6e2 640w, /media/hub/banda-cielo-960.avif?v=de11a0e9 960w, /media/hub/banda-cielo-1280.avif?v=2e0d5fe0 1280w, /media/hub/banda-cielo-1600.avif?v=62796281 1600w, /media/hub/banda-cielo-2048.avif?v=cd03ac37 2048w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/hub/banda-cielo-640.webp?v=62849298 640w, /media/hub/banda-cielo-960.webp?v=5141367e 960w, /media/hub/banda-cielo-1280.webp?v=0d0d4a6f 1280w, /media/hub/banda-cielo-1600.webp?v=12546cdd 1600w, /media/hub/banda-cielo-2048.webp?v=3c1b3720 2048w' },
    ],
    widths: [640, 960, 1280, 1600, 2048],
  },
  'hub/card-buenos-aires': {
    key: 'hub/card-buenos-aires',
    alt: 'El Obelisco de Buenos Aires sobre la Avenida 9 de Julio con jacarandás en flor',
    origen: 'generado',
    seccion: 'Hub · Ruta del Despertar · tarjeta 1 (arco vertical)',
    width: 960,
    height: 1440,
    aspectRatio: 0.6667,
    transparente: false,
    src: '/media/hub/card-buenos-aires-960.jpg?v=d98d2db6',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/hub/card-buenos-aires-320.jpg?v=ad85fb44 320w, /media/hub/card-buenos-aires-480.jpg?v=0a4a0ac4 480w, /media/hub/card-buenos-aires-640.jpg?v=0ca364f4 640w, /media/hub/card-buenos-aires-960.jpg?v=d98d2db6 960w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/hub/card-buenos-aires-320.avif?v=a5546c5a 320w, /media/hub/card-buenos-aires-480.avif?v=e31b95f1 480w, /media/hub/card-buenos-aires-640.avif?v=966a0f19 640w, /media/hub/card-buenos-aires-960.avif?v=22d929ad 960w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/hub/card-buenos-aires-320.webp?v=0c2aefc0 320w, /media/hub/card-buenos-aires-480.webp?v=3e3b2892 480w, /media/hub/card-buenos-aires-640.webp?v=8dd18f72 640w, /media/hub/card-buenos-aires-960.webp?v=cd32106c 960w' },
    ],
    widths: [320, 480, 640, 960],
  },
  'hub/card-bogota': {
    key: 'hub/card-bogota',
    alt: 'Bogotá vista desde la montaña con una iglesia blanca de torre alta y la ciudad al fondo',
    origen: 'generado',
    seccion: 'Hub · Ruta del Despertar · tarjeta 2 (arco vertical)',
    width: 960,
    height: 1440,
    aspectRatio: 0.6667,
    transparente: false,
    src: '/media/hub/card-bogota-960.jpg?v=3008da9e',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/hub/card-bogota-320.jpg?v=a7220845 320w, /media/hub/card-bogota-480.jpg?v=26a632e0 480w, /media/hub/card-bogota-640.jpg?v=26ec4012 640w, /media/hub/card-bogota-960.jpg?v=3008da9e 960w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/hub/card-bogota-320.avif?v=ca35dc0b 320w, /media/hub/card-bogota-480.avif?v=a6ceeded 480w, /media/hub/card-bogota-640.avif?v=c1688ad9 640w, /media/hub/card-bogota-960.avif?v=002368b5 960w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/hub/card-bogota-320.webp?v=217e44a6 320w, /media/hub/card-bogota-480.webp?v=48064d2a 480w, /media/hub/card-bogota-640.webp?v=56ebc48a 640w, /media/hub/card-bogota-960.webp?v=b34ccb1d 960w' },
    ],
    widths: [320, 480, 640, 960],
  },
  'hub/card-miami': {
    key: 'hub/card-miami',
    alt: 'Palmeras de Miami frente al skyline y un puente sobre agua turquesa',
    origen: 'generado',
    seccion: 'Hub · Ruta del Despertar · tarjeta 3 (arco vertical)',
    width: 960,
    height: 1440,
    aspectRatio: 0.6667,
    transparente: false,
    src: '/media/hub/card-miami-960.jpg?v=2ca65741',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/hub/card-miami-320.jpg?v=769818a3 320w, /media/hub/card-miami-480.jpg?v=a60c0ff3 480w, /media/hub/card-miami-640.jpg?v=4e5e4386 640w, /media/hub/card-miami-960.jpg?v=2ca65741 960w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/hub/card-miami-320.avif?v=67484712 320w, /media/hub/card-miami-480.avif?v=6476252f 480w, /media/hub/card-miami-640.avif?v=e7e7fec0 640w, /media/hub/card-miami-960.avif?v=f6f4801f 960w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/hub/card-miami-320.webp?v=95605ccb 320w, /media/hub/card-miami-480.webp?v=cca7aa23 480w, /media/hub/card-miami-640.webp?v=e95d0aef 640w, /media/hub/card-miami-960.webp?v=5856999a 960w' },
    ],
    widths: [320, 480, 640, 960],
  },
  'hub/card-mexico': {
    key: 'hub/card-mexico',
    alt: 'El Ángel de la Independencia dorado frente a los rascacielos de Ciudad de México',
    origen: 'generado',
    seccion: 'Hub · Ruta del Despertar · tarjeta 4 (arco vertical)',
    width: 960,
    height: 1440,
    aspectRatio: 0.6667,
    transparente: false,
    src: '/media/hub/card-mexico-960.jpg?v=e24648b1',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/hub/card-mexico-320.jpg?v=29e20c58 320w, /media/hub/card-mexico-480.jpg?v=020afc52 480w, /media/hub/card-mexico-640.jpg?v=1b94c5ab 640w, /media/hub/card-mexico-960.jpg?v=e24648b1 960w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/hub/card-mexico-320.avif?v=97579444 320w, /media/hub/card-mexico-480.avif?v=bf4918d7 480w, /media/hub/card-mexico-640.avif?v=9507666a 640w, /media/hub/card-mexico-960.avif?v=4353ebbf 960w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/hub/card-mexico-320.webp?v=aa5a33eb 320w, /media/hub/card-mexico-480.webp?v=e8ff7329 480w, /media/hub/card-mexico-640.webp?v=eca926cd 640w, /media/hub/card-mexico-960.webp?v=d078992e 960w' },
    ],
    widths: [320, 480, 640, 960],
  },
  'hub/card-cancun': {
    key: 'hub/card-cancun',
    alt: 'Vista aérea de la costa de Cancún con playa blanca y mar turquesa',
    origen: 'generado',
    seccion: 'Hub · Ruta del Despertar · tarjeta 5 VIP (arco vertical)',
    width: 960,
    height: 1440,
    aspectRatio: 0.6667,
    transparente: false,
    src: '/media/hub/card-cancun-960.jpg?v=119f7928',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/hub/card-cancun-320.jpg?v=3ed3ea8c 320w, /media/hub/card-cancun-480.jpg?v=84752c8d 480w, /media/hub/card-cancun-640.jpg?v=4180626c 640w, /media/hub/card-cancun-960.jpg?v=119f7928 960w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/hub/card-cancun-320.avif?v=b344ae60 320w, /media/hub/card-cancun-480.avif?v=6dac6e29 480w, /media/hub/card-cancun-640.avif?v=71f290fd 640w, /media/hub/card-cancun-960.avif?v=4fd467b2 960w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/hub/card-cancun-320.webp?v=1d273e19 320w, /media/hub/card-cancun-480.webp?v=dd57524f 480w, /media/hub/card-cancun-640.webp?v=4e61240a 640w, /media/hub/card-cancun-960.webp?v=6cfbe360 960w' },
    ],
    widths: [320, 480, 640, 960],
  },
  'buenos-aires/hero': {
    key: 'buenos-aires/hero',
    alt: 'El Obelisco de Buenos Aires recortado contra un cielo luminoso',
    origen: 'generado',
    seccion: 'Buenos Aires · hero (Obelisco a la izquierda, aire a la derecha)',
    width: 1536,
    height: 1024,
    aspectRatio: 1.5,
    transparente: false,
    src: '/media/buenos-aires/hero-1536.jpg?v=59315498',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/buenos-aires/hero-640.jpg?v=8c3d27e5 640w, /media/buenos-aires/hero-960.jpg?v=019d858c 960w, /media/buenos-aires/hero-1280.jpg?v=5253fead 1280w, /media/buenos-aires/hero-1536.jpg?v=59315498 1536w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/buenos-aires/hero-640.avif?v=d6b99287 640w, /media/buenos-aires/hero-960.avif?v=48acc6d1 960w, /media/buenos-aires/hero-1280.avif?v=33203710 1280w, /media/buenos-aires/hero-1536.avif?v=4d77a8d6 1536w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/buenos-aires/hero-640.webp?v=2e61427f 640w, /media/buenos-aires/hero-960.webp?v=3ecc835a 960w, /media/buenos-aires/hero-1280.webp?v=c1f9e894 1280w, /media/buenos-aires/hero-1536.webp?v=96461177 1536w' },
    ],
    widths: [640, 960, 1280, 1536],
  },
  'buenos-aires/bandera-seda': {
    key: 'buenos-aires/bandera-seda',
    alt: 'Bandera argentina de seda luminosa ondeando suavemente',
    origen: 'generado',
    seccion: 'Buenos Aires · velo de bandera argentina (zona media-izquierda y banda de entradas)',
    width: 2048,
    height: 1024,
    aspectRatio: 2.0,
    transparente: false,
    src: '/media/buenos-aires/bandera-seda-2048.jpg?v=6c61e68d',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/buenos-aires/bandera-seda-640.jpg?v=2f498f11 640w, /media/buenos-aires/bandera-seda-960.jpg?v=51f3ea50 960w, /media/buenos-aires/bandera-seda-1280.jpg?v=e3d862bd 1280w, /media/buenos-aires/bandera-seda-1600.jpg?v=4357b5a4 1600w, /media/buenos-aires/bandera-seda-2048.jpg?v=6c61e68d 2048w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/buenos-aires/bandera-seda-640.avif?v=1d4ae2ce 640w, /media/buenos-aires/bandera-seda-960.avif?v=a0efbaa6 960w, /media/buenos-aires/bandera-seda-1280.avif?v=71548a90 1280w, /media/buenos-aires/bandera-seda-1600.avif?v=4886ddd8 1600w, /media/buenos-aires/bandera-seda-2048.avif?v=81e05b53 2048w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/buenos-aires/bandera-seda-640.webp?v=cf6543f1 640w, /media/buenos-aires/bandera-seda-960.webp?v=22dc9f23 960w, /media/buenos-aires/bandera-seda-1280.webp?v=35055380 1280w, /media/buenos-aires/bandera-seda-1600.webp?v=ffc40523 1600w, /media/buenos-aires/bandera-seda-2048.webp?v=486bc3df 2048w' },
    ],
    widths: [640, 960, 1280, 1600, 2048],
  },
  'buenos-aires/camara-domo': {
    key: 'buenos-aires/camara-domo',
    alt: 'Domo geodésico de cristal sobre una plataforma circular junto al agua en calma',
    origen: 'generado',
    seccion: 'Buenos Aires · banda \'Cámara Cuántica\' (domo a la izquierda)',
    width: 2048,
    height: 1024,
    aspectRatio: 2.0,
    transparente: false,
    src: '/media/buenos-aires/camara-domo-2048.jpg?v=e364897d',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/buenos-aires/camara-domo-640.jpg?v=3bfa71e6 640w, /media/buenos-aires/camara-domo-960.jpg?v=724f6d99 960w, /media/buenos-aires/camara-domo-1280.jpg?v=be1fa6ee 1280w, /media/buenos-aires/camara-domo-1600.jpg?v=da869001 1600w, /media/buenos-aires/camara-domo-2048.jpg?v=e364897d 2048w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/buenos-aires/camara-domo-640.avif?v=c7a4a2a7 640w, /media/buenos-aires/camara-domo-960.avif?v=c11eea1f 960w, /media/buenos-aires/camara-domo-1280.avif?v=21f4bbd1 1280w, /media/buenos-aires/camara-domo-1600.avif?v=330cb45d 1600w, /media/buenos-aires/camara-domo-2048.avif?v=ece68136 2048w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/buenos-aires/camara-domo-640.webp?v=b48010bd 640w, /media/buenos-aires/camara-domo-960.webp?v=f35d4854 960w, /media/buenos-aires/camara-domo-1280.webp?v=e8a0eb9f 1280w, /media/buenos-aires/camara-domo-1600.webp?v=ce500618 1600w, /media/buenos-aires/camara-domo-2048.webp?v=ae732a9b 2048w' },
    ],
    widths: [640, 960, 1280, 1600, 2048],
  },
  'buenos-aires/escalera-luz': {
    key: 'buenos-aires/escalera-luz',
    alt: 'Escalera de piedra blanca que asciende hacia nubes luminosas',
    origen: 'generado',
    seccion: 'Buenos Aires · bloque Azrahel (ornamento a la derecha)',
    width: 1024,
    height: 1536,
    aspectRatio: 0.6667,
    transparente: false,
    src: '/media/buenos-aires/escalera-luz-1024.jpg?v=4dd86d92',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/buenos-aires/escalera-luz-480.jpg?v=a36eb625 480w, /media/buenos-aires/escalera-luz-720.jpg?v=1fda8b7e 720w, /media/buenos-aires/escalera-luz-960.jpg?v=850ee6d5 960w, /media/buenos-aires/escalera-luz-1024.jpg?v=4dd86d92 1024w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/buenos-aires/escalera-luz-480.avif?v=9426880f 480w, /media/buenos-aires/escalera-luz-720.avif?v=31e1eef3 720w, /media/buenos-aires/escalera-luz-960.avif?v=1c988afc 960w, /media/buenos-aires/escalera-luz-1024.avif?v=6b490b52 1024w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/buenos-aires/escalera-luz-480.webp?v=33e0a10c 480w, /media/buenos-aires/escalera-luz-720.webp?v=426eb609 720w, /media/buenos-aires/escalera-luz-960.webp?v=c48f8b7f 960w, /media/buenos-aires/escalera-luz-1024.webp?v=1fdbff63 1024w' },
    ],
    widths: [480, 720, 960, 1024],
  },
  'bogota/carolina-salazar': {
    key: 'bogota/carolina-salazar',
    alt: 'Carolina Salazar, invitada especial en Bogotá, sonriendo con blusa blanca junto a plantas y una vela, con el logo de AYS Amor y Ser',
    origen: 'real',
    seccion: 'Bogotá · invitada especial (foto del cliente, 17-sep-2026)',
    width: 1024,
    height: 1536,
    aspectRatio: 0.6667,
    transparente: false,
    src: '/media/bogota/carolina-salazar-1024.jpg?v=c3f38791',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/bogota/carolina-salazar-480.jpg?v=30fad735 480w, /media/bogota/carolina-salazar-768.jpg?v=c65a8e1e 768w, /media/bogota/carolina-salazar-1024.jpg?v=c3f38791 1024w',
    sources: [
      { format: 'webp', type: MIME.webp, srcSet: '/media/bogota/carolina-salazar-480.webp?v=eacdca21 480w, /media/bogota/carolina-salazar-768.webp?v=842b32f8 768w, /media/bogota/carolina-salazar-1024.webp?v=cc28fa0a 1024w' },
    ],
    widths: [480, 768, 1024],
  },
  'buenos-aires/sil-romero-pato-brusa': {
    key: 'buenos-aires/sil-romero-pato-brusa',
    alt: 'Sil Romero y Pato Brusa, de Para Compartir, vestidas de blanco y apoyadas en la mano, con el logo de Para Compartir',
    origen: 'real',
    seccion: 'Buenos Aires · invitadas especiales (foto del cliente, 20-oct-2026)',
    width: 901,
    height: 1280,
    aspectRatio: 0.7039,
    transparente: false,
    src: '/media/buenos-aires/sil-romero-pato-brusa-901.jpg?v=de661c24',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/buenos-aires/sil-romero-pato-brusa-480.jpg?v=76100c4b 480w, /media/buenos-aires/sil-romero-pato-brusa-720.jpg?v=4f48f812 720w, /media/buenos-aires/sil-romero-pato-brusa-901.jpg?v=de661c24 901w',
    sources: [
      { format: 'webp', type: MIME.webp, srcSet: '/media/buenos-aires/sil-romero-pato-brusa-480.webp?v=fb4027f8 480w, /media/buenos-aires/sil-romero-pato-brusa-720.webp?v=74a507ef 720w, /media/buenos-aires/sil-romero-pato-brusa-901.webp?v=55a83797 901w' },
    ],
    widths: [480, 720, 901],
  },
  'bogota/akasha-logo': {
    key: 'bogota/akasha-logo',
    alt: 'Logo de AKASHA Despertar Espiritual',
    origen: 'real',
    seccion: 'Bogotá · colaborador (logo del cliente, 17-sep-2026, fondo gris retirado)',
    width: 480,
    height: 458,
    aspectRatio: 1.048,
    transparente: true,
    src: '/media/bogota/akasha-logo-480.png?v=ef393e2c',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/bogota/akasha-logo-240.png?v=3de6cb2e 240w, /media/bogota/akasha-logo-480.png?v=ef393e2c 480w',
    sources: [
      { format: 'webp', type: MIME.webp, srcSet: '/media/bogota/akasha-logo-240.webp?v=aff0827b 240w, /media/bogota/akasha-logo-480.webp?v=fe085c47 480w' },
    ],
    widths: [240, 480],
  },
  'bogota/hero': {
    key: 'bogota/hero',
    alt: 'Panorámica de Bogotá desde la montaña con una iglesia blanca en la cima',
    origen: 'generado',
    seccion: 'Bogotá · hero (montaña e iglesia a la derecha, Azrahel a la izquierda)',
    width: 1536,
    height: 1024,
    aspectRatio: 1.5,
    transparente: false,
    src: '/media/bogota/hero-1536.jpg?v=c7f1667b',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/bogota/hero-640.jpg?v=b6fd65dc 640w, /media/bogota/hero-960.jpg?v=b9e265b7 960w, /media/bogota/hero-1280.jpg?v=606a241e 1280w, /media/bogota/hero-1536.jpg?v=c7f1667b 1536w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/bogota/hero-640.avif?v=05bd3935 640w, /media/bogota/hero-960.avif?v=9efa0436 960w, /media/bogota/hero-1280.avif?v=d84ee427 1280w, /media/bogota/hero-1536.avif?v=d355296c 1536w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/bogota/hero-640.webp?v=06de06e9 640w, /media/bogota/hero-960.webp?v=be990a16 960w, /media/bogota/hero-1280.webp?v=0ea2f3a5 1280w, /media/bogota/hero-1536.webp?v=41734f01 1536w' },
    ],
    widths: [640, 960, 1280, 1536],
  },
  'bogota/camara': {
    key: 'bogota/camara',
    alt: 'Recinto hexagonal de cristal con marco dorado iluminado desde dentro, con velas y flores',
    origen: 'generado',
    seccion: 'Bogotá · banda \'Cámara Cuántica\' (recinto dorado a la derecha)',
    width: 1280,
    height: 853,
    aspectRatio: 1.5006,
    transparente: false,
    src: '/media/bogota/camara-1280.jpg?v=1112f0ae',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/bogota/camara-480.jpg?v=ea202633 480w, /media/bogota/camara-720.jpg?v=096cc085 720w, /media/bogota/camara-960.jpg?v=3b73be30 960w, /media/bogota/camara-1280.jpg?v=1112f0ae 1280w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/bogota/camara-480.avif?v=5d870f9e 480w, /media/bogota/camara-720.avif?v=80c390aa 720w, /media/bogota/camara-960.avif?v=26452ba2 960w, /media/bogota/camara-1280.avif?v=35208281 1280w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/bogota/camara-480.webp?v=17f4076e 480w, /media/bogota/camara-720.webp?v=7361c8e6 720w, /media/bogota/camara-960.webp?v=a2ade81f 960w, /media/bogota/camara-1280.webp?v=5595c5b7 1280w' },
    ],
    widths: [480, 720, 960, 1280],
  },
  'bogota/entradas-cielo': {
    key: 'bogota/entradas-cielo',
    alt: 'Cielo azul suave con nubes que dibujan un corazón de luz',
    origen: 'generado',
    seccion: 'Bogotá · tarjeta \'Entradas\' (fondo de cielo con corazón de luz)',
    width: 1024,
    height: 1024,
    aspectRatio: 1.0,
    transparente: false,
    src: '/media/bogota/entradas-cielo-1024.jpg?v=ad9c014e',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/bogota/entradas-cielo-480.jpg?v=db5511bb 480w, /media/bogota/entradas-cielo-720.jpg?v=6f1461c5 720w, /media/bogota/entradas-cielo-960.jpg?v=c3a86584 960w, /media/bogota/entradas-cielo-1024.jpg?v=ad9c014e 1024w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/bogota/entradas-cielo-480.avif?v=bb8a5486 480w, /media/bogota/entradas-cielo-720.avif?v=9820e1b2 720w, /media/bogota/entradas-cielo-960.avif?v=824a55d0 960w, /media/bogota/entradas-cielo-1024.avif?v=91c09808 1024w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/bogota/entradas-cielo-480.webp?v=dbb6f03f 480w, /media/bogota/entradas-cielo-720.webp?v=703cf1ab 720w, /media/bogota/entradas-cielo-960.webp?v=666696b9 960w, /media/bogota/entradas-cielo-1024.webp?v=d8b45f7c 1024w' },
    ],
    widths: [480, 720, 960, 1024],
  },
  'bogota/cierre-panorama': {
    key: 'bogota/cierre-panorama',
    alt: 'Panorámica muy suave del valle de Bogotá con montañas al fondo',
    origen: 'generado',
    seccion: 'Bogotá · banda de cierre \'Recuerda. Sana. Vuelve a Casa.\'',
    width: 2048,
    height: 1024,
    aspectRatio: 2.0,
    transparente: false,
    src: '/media/bogota/cierre-panorama-2048.jpg?v=ee5a5e71',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/bogota/cierre-panorama-640.jpg?v=819ca032 640w, /media/bogota/cierre-panorama-960.jpg?v=659c49d5 960w, /media/bogota/cierre-panorama-1280.jpg?v=d5b15f8d 1280w, /media/bogota/cierre-panorama-1600.jpg?v=011e55f0 1600w, /media/bogota/cierre-panorama-2048.jpg?v=ee5a5e71 2048w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/bogota/cierre-panorama-640.avif?v=8ddbe98a 640w, /media/bogota/cierre-panorama-960.avif?v=2f3db42e 960w, /media/bogota/cierre-panorama-1280.avif?v=398abc6b 1280w, /media/bogota/cierre-panorama-1600.avif?v=b7f4f65d 1600w, /media/bogota/cierre-panorama-2048.avif?v=dfeac0c2 2048w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/bogota/cierre-panorama-640.webp?v=5675a04b 640w, /media/bogota/cierre-panorama-960.webp?v=80371725 960w, /media/bogota/cierre-panorama-1280.webp?v=b7dd56da 1280w, /media/bogota/cierre-panorama-1600.webp?v=4a850302 1600w, /media/bogota/cierre-panorama-2048.webp?v=d4efd44d 2048w' },
    ],
    widths: [640, 960, 1280, 1600, 2048],
  },
  'miami/hero': {
    key: 'miami/hero',
    alt: 'Fachada art déco pastel de Miami Beach con palmeras y un descapotable clásico turquesa',
    origen: 'generado',
    seccion: 'Miami · hero (hotel art déco y coche clásico a la izquierda)',
    width: 1536,
    height: 1024,
    aspectRatio: 1.5,
    transparente: false,
    src: '/media/miami/hero-1536.jpg?v=2a28a7fa',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/miami/hero-640.jpg?v=7dbe67bd 640w, /media/miami/hero-960.jpg?v=6f1c3b54 960w, /media/miami/hero-1280.jpg?v=d400979e 1280w, /media/miami/hero-1536.jpg?v=2a28a7fa 1536w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/miami/hero-640.avif?v=0162905c 640w, /media/miami/hero-960.avif?v=ea0e1431 960w, /media/miami/hero-1280.avif?v=c5f903be 1280w, /media/miami/hero-1536.avif?v=0fea1486 1536w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/miami/hero-640.webp?v=25814033 640w, /media/miami/hero-960.webp?v=d48ce4c6 960w, /media/miami/hero-1280.webp?v=1448896f 1280w, /media/miami/hero-1536.webp?v=5bb03694 1536w' },
    ],
    widths: [640, 960, 1280, 1536],
  },
  'miami/entrada-general': {
    key: 'miami/entrada-general',
    alt: 'Fachada art déco pastel de Miami Beach enmarcada por palmeras',
    origen: 'generado',
    seccion: 'Miami · tarjeta de entrada GENERAL (imagen lateral)',
    width: 1024,
    height: 1024,
    aspectRatio: 1.0,
    transparente: false,
    src: '/media/miami/entrada-general-1024.jpg?v=56af9fd0',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/miami/entrada-general-480.jpg?v=ccc779d6 480w, /media/miami/entrada-general-720.jpg?v=de7bd398 720w, /media/miami/entrada-general-960.jpg?v=6634d93d 960w, /media/miami/entrada-general-1024.jpg?v=56af9fd0 1024w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/miami/entrada-general-480.avif?v=08111438 480w, /media/miami/entrada-general-720.avif?v=074989f0 720w, /media/miami/entrada-general-960.avif?v=37209e6f 960w, /media/miami/entrada-general-1024.avif?v=480e1b25 1024w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/miami/entrada-general-480.webp?v=405afcf8 480w, /media/miami/entrada-general-720.webp?v=4711ff20 720w, /media/miami/entrada-general-960.webp?v=6f8ed82f 960w, /media/miami/entrada-general-1024.webp?v=78d5147a 1024w' },
    ],
    widths: [480, 720, 960, 1024],
  },
  'miami/entrada-vip': {
    key: 'miami/entrada-vip',
    alt: 'Terraza lounge de lujo frente al mar con sofá curvo color crema y palmeras',
    origen: 'generado',
    seccion: 'Miami · tarjeta de entrada VIP (imagen lateral)',
    width: 1024,
    height: 1024,
    aspectRatio: 1.0,
    transparente: false,
    src: '/media/miami/entrada-vip-1024.jpg?v=46ac964b',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/miami/entrada-vip-480.jpg?v=63f00729 480w, /media/miami/entrada-vip-720.jpg?v=dbfbe344 720w, /media/miami/entrada-vip-960.jpg?v=1aea7cf0 960w, /media/miami/entrada-vip-1024.jpg?v=46ac964b 1024w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/miami/entrada-vip-480.avif?v=10934f23 480w, /media/miami/entrada-vip-720.avif?v=66c79666 720w, /media/miami/entrada-vip-960.avif?v=f3cf2663 960w, /media/miami/entrada-vip-1024.avif?v=af4393a5 1024w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/miami/entrada-vip-480.webp?v=3251cd04 480w, /media/miami/entrada-vip-720.webp?v=5cbd4261 720w, /media/miami/entrada-vip-960.webp?v=02ceabbf 960w, /media/miami/entrada-vip-1024.webp?v=6ea93675 1024w' },
    ],
    widths: [480, 720, 960, 1024],
  },
  'miami/camara': {
    key: 'miami/camara',
    alt: 'Sala blanca y dorada con un portal circular luminoso y vistas al mar',
    origen: 'generado',
    seccion: 'Miami · tarjeta \'Cámara Cuántica\' (imagen de la tarjeta derecha)',
    width: 1280,
    height: 853,
    aspectRatio: 1.5006,
    transparente: false,
    src: '/media/miami/camara-1280.jpg?v=183affd2',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/miami/camara-480.jpg?v=e59e1823 480w, /media/miami/camara-720.jpg?v=61964ad6 720w, /media/miami/camara-960.jpg?v=4f486baa 960w, /media/miami/camara-1280.jpg?v=183affd2 1280w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/miami/camara-480.avif?v=f7dd6fff 480w, /media/miami/camara-720.avif?v=739d7d49 720w, /media/miami/camara-960.avif?v=eb30a933 960w, /media/miami/camara-1280.avif?v=0b66f54d 1280w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/miami/camara-480.webp?v=75c7fb4e 480w, /media/miami/camara-720.webp?v=d4c5b6c9 720w, /media/miami/camara-960.webp?v=cf638587 960w, /media/miami/camara-1280.webp?v=8e33b096 1280w' },
    ],
    widths: [480, 720, 960, 1280],
  },
  'miami/cierre-palmeras': {
    key: 'miami/cierre-palmeras',
    alt: 'Hojas de palmera suaves sobre un fondo turquesa y crema luminoso',
    origen: 'generado',
    seccion: 'Miami · banda de cierre (hojas de palmera y franja turquesa)',
    width: 2048,
    height: 1024,
    aspectRatio: 2.0,
    transparente: false,
    src: '/media/miami/cierre-palmeras-2048.jpg?v=63a779de',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/miami/cierre-palmeras-640.jpg?v=d034c6ab 640w, /media/miami/cierre-palmeras-960.jpg?v=3ecb7f23 960w, /media/miami/cierre-palmeras-1280.jpg?v=e1e9b455 1280w, /media/miami/cierre-palmeras-1600.jpg?v=cc755a43 1600w, /media/miami/cierre-palmeras-2048.jpg?v=63a779de 2048w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/miami/cierre-palmeras-640.avif?v=cfc4c611 640w, /media/miami/cierre-palmeras-960.avif?v=ce1526d3 960w, /media/miami/cierre-palmeras-1280.avif?v=646dc992 1280w, /media/miami/cierre-palmeras-1600.avif?v=a62948e8 1600w, /media/miami/cierre-palmeras-2048.avif?v=1121c0f7 2048w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/miami/cierre-palmeras-640.webp?v=c93fa6f9 640w, /media/miami/cierre-palmeras-960.webp?v=4d2401d4 960w, /media/miami/cierre-palmeras-1280.webp?v=51ab5b90 1280w, /media/miami/cierre-palmeras-1600.webp?v=36a0560f 1600w, /media/miami/cierre-palmeras-2048.webp?v=ca20e016 2048w' },
    ],
    widths: [640, 960, 1280, 1600, 2048],
  },
  'mexico/hero': {
    key: 'mexico/hero',
    alt: 'El Ángel de la Independencia con la bandera de México y los rascacielos de la ciudad',
    origen: 'generado',
    seccion: 'Ciudad de México · hero (Ángel de la Independencia y bandera a la izquierda)',
    width: 1536,
    height: 1024,
    aspectRatio: 1.5,
    transparente: false,
    src: '/media/mexico/hero-1536.jpg?v=ba76675d',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/mexico/hero-640.jpg?v=61c10691 640w, /media/mexico/hero-960.jpg?v=d8897ac6 960w, /media/mexico/hero-1280.jpg?v=8423e1ed 1280w, /media/mexico/hero-1536.jpg?v=ba76675d 1536w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/mexico/hero-640.avif?v=89443769 640w, /media/mexico/hero-960.avif?v=56e6535a 960w, /media/mexico/hero-1280.avif?v=73110f82 1280w, /media/mexico/hero-1536.avif?v=1ed09a54 1536w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/mexico/hero-640.webp?v=2f767c18 640w, /media/mexico/hero-960.webp?v=2dee0364 960w, /media/mexico/hero-1280.webp?v=373c6071 1280w, /media/mexico/hero-1536.webp?v=63dc561d 1536w' },
    ],
    widths: [640, 960, 1280, 1536],
  },
  'mexico/camara': {
    key: 'mexico/camara',
    alt: 'Pabellón cilíndrico de cristal con marco dorado iluminado desde dentro',
    origen: 'generado',
    seccion: 'Ciudad de México · columna central \'Cámara Cuántica\'',
    width: 1024,
    height: 1536,
    aspectRatio: 0.6667,
    transparente: false,
    src: '/media/mexico/camara-1024.jpg?v=3ff8bd20',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/mexico/camara-480.jpg?v=4d6473cf 480w, /media/mexico/camara-720.jpg?v=f28cc2f8 720w, /media/mexico/camara-960.jpg?v=f20e6ff7 960w, /media/mexico/camara-1024.jpg?v=3ff8bd20 1024w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/mexico/camara-480.avif?v=2c4855f1 480w, /media/mexico/camara-720.avif?v=8267ce85 720w, /media/mexico/camara-960.avif?v=1113c770 960w, /media/mexico/camara-1024.avif?v=f54d9843 1024w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/mexico/camara-480.webp?v=ab70e113 480w, /media/mexico/camara-720.webp?v=4e9854b8 720w, /media/mexico/camara-960.webp?v=b90d2d75 960w, /media/mexico/camara-1024.webp?v=630a6901 1024w' },
    ],
    widths: [480, 720, 960, 1024],
  },
  'mexico/viaje-skyline': {
    key: 'mexico/viaje-skyline',
    alt: 'Skyline de Ciudad de México muy suave con un volcán al fondo',
    origen: 'generado',
    seccion: 'Ciudad de México · columna \'Un viaje de dos días\' (skyline suave al pie)',
    width: 1280,
    height: 853,
    aspectRatio: 1.5006,
    transparente: false,
    src: '/media/mexico/viaje-skyline-1280.jpg?v=6fc77bbd',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/mexico/viaje-skyline-480.jpg?v=f0efa8c0 480w, /media/mexico/viaje-skyline-720.jpg?v=1cf329e4 720w, /media/mexico/viaje-skyline-960.jpg?v=c0321058 960w, /media/mexico/viaje-skyline-1280.jpg?v=6fc77bbd 1280w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/mexico/viaje-skyline-480.avif?v=a56b1b15 480w, /media/mexico/viaje-skyline-720.avif?v=204e4c37 720w, /media/mexico/viaje-skyline-960.avif?v=8c5157ad 960w, /media/mexico/viaje-skyline-1280.avif?v=ac9a1daa 1280w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/mexico/viaje-skyline-480.webp?v=a28f7cc4 480w, /media/mexico/viaje-skyline-720.webp?v=2431b4ce 720w, /media/mexico/viaje-skyline-960.webp?v=e9da7ecb 960w, /media/mexico/viaje-skyline-1280.webp?v=daedb842 1280w' },
    ],
    widths: [480, 720, 960, 1280],
  },
  'mexico/ornamento-hojas': {
    key: 'mexico/ornamento-hojas',
    alt: 'Ramas botánicas doradas y verde oliva a los lados sobre fondo crema',
    origen: 'generado',
    seccion: 'Ciudad de México · banda de entradas (hojas doradas en las esquinas)',
    width: 2048,
    height: 1024,
    aspectRatio: 2.0,
    transparente: false,
    src: '/media/mexico/ornamento-hojas-2048.jpg?v=023195c3',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/mexico/ornamento-hojas-640.jpg?v=fac2c951 640w, /media/mexico/ornamento-hojas-960.jpg?v=8dfa0551 960w, /media/mexico/ornamento-hojas-1280.jpg?v=b1c01c0b 1280w, /media/mexico/ornamento-hojas-1600.jpg?v=6072d551 1600w, /media/mexico/ornamento-hojas-2048.jpg?v=023195c3 2048w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/mexico/ornamento-hojas-640.avif?v=97c87015 640w, /media/mexico/ornamento-hojas-960.avif?v=5ba94757 960w, /media/mexico/ornamento-hojas-1280.avif?v=e9746439 1280w, /media/mexico/ornamento-hojas-1600.avif?v=886dc477 1600w, /media/mexico/ornamento-hojas-2048.avif?v=f98654e2 2048w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/mexico/ornamento-hojas-640.webp?v=8a35c44e 640w, /media/mexico/ornamento-hojas-960.webp?v=4969b08d 960w, /media/mexico/ornamento-hojas-1280.webp?v=14c2e516 1280w, /media/mexico/ornamento-hojas-1600.webp?v=505fef09 1600w, /media/mexico/ornamento-hojas-2048.webp?v=d41f732c 2048w' },
    ],
    widths: [640, 960, 1280, 1600, 2048],
  },
  'cancun/hero': {
    key: 'cancun/hero',
    alt: 'Costa de la Riviera Maya con mar turquesa, palmeras y una ruina maya sobre el acantilado',
    origen: 'generado',
    seccion: 'Cancún VIP · hero (cortinas, mar turquesa y ruina maya)',
    width: 1536,
    height: 1024,
    aspectRatio: 1.5,
    transparente: false,
    src: '/media/cancun/hero-1536.jpg?v=f2d9fd31',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/cancun/hero-640.jpg?v=15c377f8 640w, /media/cancun/hero-960.jpg?v=4e6079c4 960w, /media/cancun/hero-1280.jpg?v=4aa91658 1280w, /media/cancun/hero-1536.jpg?v=f2d9fd31 1536w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/hero-640.avif?v=b8825a75 640w, /media/cancun/hero-960.avif?v=e9848c8b 960w, /media/cancun/hero-1280.avif?v=295f9fd9 1280w, /media/cancun/hero-1536.avif?v=da81ed5e 1536w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/hero-640.webp?v=8ded08a6 640w, /media/cancun/hero-960.webp?v=03875d76 960w, /media/cancun/hero-1280.webp?v=d3b3444b 1280w, /media/cancun/hero-1536.webp?v=6d778ea9 1536w' },
    ],
    widths: [640, 960, 1280, 1536],
  },
  'cancun/thumb-amanecer': {
    key: 'cancun/thumb-amanecer',
    alt: 'Amanecer sobre el mar Caribe con un camino dorado de luz sobre las olas',
    origen: 'generado',
    seccion: 'Cancún VIP · programa Día 1 · \'Amanecer Sagrado\'',
    width: 384,
    height: 384,
    aspectRatio: 1.0,
    transparente: false,
    src: '/media/cancun/thumb-amanecer-384.jpg?v=b4bb2327',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/cancun/thumb-amanecer-128.jpg?v=4ba22790 128w, /media/cancun/thumb-amanecer-192.jpg?v=086c2aba 192w, /media/cancun/thumb-amanecer-256.jpg?v=98d72e27 256w, /media/cancun/thumb-amanecer-384.jpg?v=b4bb2327 384w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/thumb-amanecer-128.avif?v=44a2dc2a 128w, /media/cancun/thumb-amanecer-192.avif?v=4b67f9e3 192w, /media/cancun/thumb-amanecer-256.avif?v=83372bcd 256w, /media/cancun/thumb-amanecer-384.avif?v=fde2c483 384w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/thumb-amanecer-128.webp?v=db9f2ebe 128w, /media/cancun/thumb-amanecer-192.webp?v=646fcc24 192w, /media/cancun/thumb-amanecer-256.webp?v=7e2f36fb 256w, /media/cancun/thumb-amanecer-384.webp?v=017e9097 384w' },
    ],
    widths: [128, 192, 256, 384],
  },
  'cancun/thumb-cenote': {
    key: 'cancun/thumb-cenote',
    alt: 'Cenote de agua turquesa cristalina rodeado de roca caliza y vegetación',
    origen: 'generado',
    seccion: 'Cancún VIP · programa Día 1 · \'Cenote & Purificación\'',
    width: 384,
    height: 384,
    aspectRatio: 1.0,
    transparente: false,
    src: '/media/cancun/thumb-cenote-384.jpg?v=b5869486',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/cancun/thumb-cenote-128.jpg?v=1b8227c9 128w, /media/cancun/thumb-cenote-192.jpg?v=f64a6d9c 192w, /media/cancun/thumb-cenote-256.jpg?v=dfd58a3a 256w, /media/cancun/thumb-cenote-384.jpg?v=b5869486 384w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/thumb-cenote-128.avif?v=0454f082 128w, /media/cancun/thumb-cenote-192.avif?v=01e37707 192w, /media/cancun/thumb-cenote-256.avif?v=384ffb8a 256w, /media/cancun/thumb-cenote-384.avif?v=f40643e4 384w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/thumb-cenote-128.webp?v=6b8fbd77 128w, /media/cancun/thumb-cenote-192.webp?v=5a07cf7f 192w, /media/cancun/thumb-cenote-256.webp?v=191d121e 256w, /media/cancun/thumb-cenote-384.webp?v=9146892f 384w' },
    ],
    widths: [128, 192, 256, 384],
  },
  'cancun/thumb-camara-atardecer': {
    key: 'cancun/thumb-camara-atardecer',
    alt: 'Cámara geométrica de cristal iluminada en la playa al atardecer',
    origen: 'generado',
    seccion: 'Cancún VIP · programa Día 1 · \'Cámara Cuántica al atardecer\'',
    width: 384,
    height: 384,
    aspectRatio: 1.0,
    transparente: false,
    src: '/media/cancun/thumb-camara-atardecer-384.jpg?v=fd325768',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/cancun/thumb-camara-atardecer-128.jpg?v=82ce6c65 128w, /media/cancun/thumb-camara-atardecer-192.jpg?v=2e2f7749 192w, /media/cancun/thumb-camara-atardecer-256.jpg?v=6c589fe9 256w, /media/cancun/thumb-camara-atardecer-384.jpg?v=fd325768 384w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/thumb-camara-atardecer-128.avif?v=01078b15 128w, /media/cancun/thumb-camara-atardecer-192.avif?v=b0856ba1 192w, /media/cancun/thumb-camara-atardecer-256.avif?v=c441fe6d 256w, /media/cancun/thumb-camara-atardecer-384.avif?v=a77d7155 384w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/thumb-camara-atardecer-128.webp?v=65e8415f 128w, /media/cancun/thumb-camara-atardecer-192.webp?v=dd476c24 192w, /media/cancun/thumb-camara-atardecer-256.webp?v=a7f2e150 256w, /media/cancun/thumb-camara-atardecer-384.webp?v=c3e45ce6 384w' },
    ],
    widths: [128, 192, 256, 384],
  },
  'cancun/thumb-ritual-fuego': {
    key: 'cancun/thumb-ritual-fuego',
    alt: 'Hoguera en la playa al anochecer rodeada de siluetas lejanas sentadas en círculo',
    origen: 'generado',
    seccion: 'Cancún VIP · programa Día 2 · \'Ritual del fuego en la playa\'',
    width: 384,
    height: 384,
    aspectRatio: 1.0,
    transparente: false,
    src: '/media/cancun/thumb-ritual-fuego-384.jpg?v=924463f1',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/cancun/thumb-ritual-fuego-128.jpg?v=f2d71d6c 128w, /media/cancun/thumb-ritual-fuego-192.jpg?v=1c5f665c 192w, /media/cancun/thumb-ritual-fuego-256.jpg?v=99412397 256w, /media/cancun/thumb-ritual-fuego-384.jpg?v=924463f1 384w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/thumb-ritual-fuego-128.avif?v=f8de49ba 128w, /media/cancun/thumb-ritual-fuego-192.avif?v=ced5ad68 192w, /media/cancun/thumb-ritual-fuego-256.avif?v=1d3e19a6 256w, /media/cancun/thumb-ritual-fuego-384.avif?v=97c1afca 384w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/thumb-ritual-fuego-128.webp?v=4411e507 128w, /media/cancun/thumb-ritual-fuego-192.webp?v=699cb0d1 192w, /media/cancun/thumb-ritual-fuego-256.webp?v=f6e9a5cf 256w, /media/cancun/thumb-ritual-fuego-384.webp?v=58019338 384w' },
    ],
    widths: [128, 192, 256, 384],
  },
  'cancun/thumb-guerrero': {
    key: 'cancun/thumb-guerrero',
    alt: 'Mar Caribe turquesa junto a una costa de selva verde vista desde arriba',
    origen: 'generado',
    seccion: 'Cancún VIP · programa Día 2 · \'Activación del Guerrero\'',
    width: 384,
    height: 384,
    aspectRatio: 1.0,
    transparente: false,
    src: '/media/cancun/thumb-guerrero-384.jpg?v=a0547a07',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/cancun/thumb-guerrero-128.jpg?v=24dd00d7 128w, /media/cancun/thumb-guerrero-192.jpg?v=f3d56f5f 192w, /media/cancun/thumb-guerrero-256.jpg?v=16646847 256w, /media/cancun/thumb-guerrero-384.jpg?v=a0547a07 384w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/thumb-guerrero-128.avif?v=e613321c 128w, /media/cancun/thumb-guerrero-192.avif?v=e89049d3 192w, /media/cancun/thumb-guerrero-256.avif?v=4402e8c9 256w, /media/cancun/thumb-guerrero-384.avif?v=914485bb 384w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/thumb-guerrero-128.webp?v=13f910e2 128w, /media/cancun/thumb-guerrero-192.webp?v=7c6f104e 192w, /media/cancun/thumb-guerrero-256.webp?v=61fe4095 256w, /media/cancun/thumb-guerrero-384.webp?v=abe709b6 384w' },
    ],
    widths: [128, 192, 256, 384],
  },
  'cancun/thumb-cena-gala': {
    key: 'cancun/thumb-cena-gala',
    alt: 'Mesa de cena de gala con velas encendidas, cristalería y vajilla dorada',
    origen: 'generado',
    seccion: 'Cancún VIP · programa Día 2 · \'Cena de Gala\'',
    width: 384,
    height: 384,
    aspectRatio: 1.0,
    transparente: false,
    src: '/media/cancun/thumb-cena-gala-384.jpg?v=8a290a81',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/cancun/thumb-cena-gala-128.jpg?v=3d7126a3 128w, /media/cancun/thumb-cena-gala-192.jpg?v=e87481df 192w, /media/cancun/thumb-cena-gala-256.jpg?v=f76fe24e 256w, /media/cancun/thumb-cena-gala-384.jpg?v=8a290a81 384w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/thumb-cena-gala-128.avif?v=cf8cf67e 128w, /media/cancun/thumb-cena-gala-192.avif?v=6766d920 192w, /media/cancun/thumb-cena-gala-256.avif?v=d6b3a216 256w, /media/cancun/thumb-cena-gala-384.avif?v=7b48f64d 384w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/thumb-cena-gala-128.webp?v=86e9ea5d 128w, /media/cancun/thumb-cena-gala-192.webp?v=1b863a41 192w, /media/cancun/thumb-cena-gala-256.webp?v=694bdb69 256w, /media/cancun/thumb-cena-gala-384.webp?v=16c549c3 384w' },
    ],
    widths: [128, 192, 256, 384],
  },
  'cancun/franja-mar': {
    key: 'cancun/franja-mar',
    alt: 'Mar Caribe turquesa y playa de arena blanca en calma',
    origen: 'generado',
    seccion: 'Cancún VIP · franja \'Experiencia premium · Lugares limitados\'',
    width: 2048,
    height: 1024,
    aspectRatio: 2.0,
    transparente: false,
    src: '/media/cancun/franja-mar-2048.jpg?v=daecbe44',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/cancun/franja-mar-640.jpg?v=bd2ed50d 640w, /media/cancun/franja-mar-960.jpg?v=a5ed080f 960w, /media/cancun/franja-mar-1280.jpg?v=7dbc541d 1280w, /media/cancun/franja-mar-1600.jpg?v=0f72d0e1 1600w, /media/cancun/franja-mar-2048.jpg?v=daecbe44 2048w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/franja-mar-640.avif?v=9b10caef 640w, /media/cancun/franja-mar-960.avif?v=5f412875 960w, /media/cancun/franja-mar-1280.avif?v=800c47de 1280w, /media/cancun/franja-mar-1600.avif?v=4f857241 1600w, /media/cancun/franja-mar-2048.avif?v=d43a81c3 2048w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/franja-mar-640.webp?v=6a73763e 640w, /media/cancun/franja-mar-960.webp?v=abdfc035 960w, /media/cancun/franja-mar-1280.webp?v=9785918b 1280w, /media/cancun/franja-mar-1600.webp?v=aceb3135 1600w, /media/cancun/franja-mar-2048.webp?v=515b11c8 2048w' },
    ],
    widths: [640, 960, 1280, 1600, 2048],
  },
  'cancun/tarifas-palmaia': {
    key: 'cancun/tarifas-palmaia',
    alt: 'Tarifas grupales todo incluido del Hotel Palmaïa por persona y noche, del 25 al 27 de febrero de 2027, con precio en ocupación doble e individual de cada suite y ocupación máxima por suite',
    origen: 'real',
    seccion: 'Cancún VIP · precios de las estancias en el Hotel Palmaïa (JPG del cliente, 29-sep-2026; fechas «del 25 al 27» puestas el 27-oct-2026)',
    width: 1211,
    height: 664,
    aspectRatio: 1.8238,
    transparente: false,
    src: '/media/cancun/tarifas-palmaia-1211.jpg?v=f2056892',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/cancun/tarifas-palmaia-640.jpg?v=7e5449ab 640w, /media/cancun/tarifas-palmaia-1211.jpg?v=f2056892 1211w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/tarifas-palmaia-640.avif?v=514d3cff 640w, /media/cancun/tarifas-palmaia-1211.avif?v=133588dd 1211w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/tarifas-palmaia-640.webp?v=7d833267 640w, /media/cancun/tarifas-palmaia-1211.webp?v=37ba0a54 1211w' },
    ],
    widths: [640, 1211],
  },
  'cancun/ruinas-lateral': {
    key: 'cancun/ruinas-lateral',
    alt: 'Acantilado de la Riviera Maya con una ruina maya y palmeras en primer plano',
    origen: 'generado',
    seccion: 'Cancún VIP · lateral derecho del bloque \'Programa\'',
    width: 1024,
    height: 1536,
    aspectRatio: 0.6667,
    transparente: false,
    src: '/media/cancun/ruinas-lateral-1024.jpg?v=652ac1a2',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/cancun/ruinas-lateral-480.jpg?v=e283ff3e 480w, /media/cancun/ruinas-lateral-720.jpg?v=e875732b 720w, /media/cancun/ruinas-lateral-960.jpg?v=b330701f 960w, /media/cancun/ruinas-lateral-1024.jpg?v=652ac1a2 1024w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/ruinas-lateral-480.avif?v=d2a01f9c 480w, /media/cancun/ruinas-lateral-720.avif?v=76139ce0 720w, /media/cancun/ruinas-lateral-960.avif?v=aaddaa1a 960w, /media/cancun/ruinas-lateral-1024.avif?v=bc16c469 1024w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/ruinas-lateral-480.webp?v=5af1e18b 480w, /media/cancun/ruinas-lateral-720.webp?v=47b411f9 720w, /media/cancun/ruinas-lateral-960.webp?v=9e075d38 960w, /media/cancun/ruinas-lateral-1024.webp?v=9001e81d 1024w' },
    ],
    widths: [480, 720, 960, 1024],
  },
  'comun/textura-papel': {
    key: 'comun/textura-papel',
    alt: 'Textura sutil de papel crema cálido',
    origen: 'generado',
    seccion: 'Global · textura de fondo de secciones crema',
    width: 1024,
    height: 1024,
    aspectRatio: 1.0,
    transparente: false,
    src: '/media/comun/textura-papel-1024.jpg?v=2f180916',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/comun/textura-papel-512.jpg?v=8fa886e9 512w, /media/comun/textura-papel-1024.jpg?v=2f180916 1024w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/comun/textura-papel-512.avif?v=f69a549f 512w, /media/comun/textura-papel-1024.avif?v=701f3b09 1024w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/comun/textura-papel-512.webp?v=4d81b5f9 512w, /media/comun/textura-papel-1024.webp?v=2b9a0b2c 1024w' },
    ],
    widths: [512, 1024],
  },
  'comun/fondo-claro-suave': {
    key: 'comun/fondo-claro-suave',
    alt: 'Fondo claro desenfocado de columnas de piedra pálida y tela blanca',
    origen: 'generado',
    seccion: 'Global · fondo neutro tras los retratos (Miami, cierres)',
    width: 1280,
    height: 853,
    aspectRatio: 1.5006,
    transparente: false,
    src: '/media/comun/fondo-claro-suave-1280.jpg?v=6aa2c24d',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/comun/fondo-claro-suave-480.jpg?v=5673c5b4 480w, /media/comun/fondo-claro-suave-720.jpg?v=620859c5 720w, /media/comun/fondo-claro-suave-960.jpg?v=57c8491c 960w, /media/comun/fondo-claro-suave-1280.jpg?v=6aa2c24d 1280w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/comun/fondo-claro-suave-480.avif?v=b58835ae 480w, /media/comun/fondo-claro-suave-720.avif?v=f180ef81 720w, /media/comun/fondo-claro-suave-960.avif?v=36be6ed2 960w, /media/comun/fondo-claro-suave-1280.avif?v=5c2632cf 1280w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/comun/fondo-claro-suave-480.webp?v=b4eb49d3 480w, /media/comun/fondo-claro-suave-720.webp?v=de575046 720w, /media/comun/fondo-claro-suave-960.webp?v=9c56bc7d 960w, /media/comun/fondo-claro-suave-1280.webp?v=329947f5 1280w' },
    ],
    widths: [480, 720, 960, 1280],
  },
  'comun/follaje-bokeh': {
    key: 'comun/follaje-bokeh',
    alt: 'Follaje verde desenfocado con destellos de luz',
    origen: 'generado',
    seccion: 'Global · fondo tras el retrato de Azrahel (México, Cancún)',
    width: 1280,
    height: 853,
    aspectRatio: 1.5006,
    transparente: false,
    src: '/media/comun/follaje-bokeh-1280.jpg?v=8066f87d',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/comun/follaje-bokeh-480.jpg?v=3f6bdd39 480w, /media/comun/follaje-bokeh-720.jpg?v=688d183f 720w, /media/comun/follaje-bokeh-960.jpg?v=37f9e9f2 960w, /media/comun/follaje-bokeh-1280.jpg?v=8066f87d 1280w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/comun/follaje-bokeh-480.avif?v=e27a6bca 480w, /media/comun/follaje-bokeh-720.avif?v=5d42bca0 720w, /media/comun/follaje-bokeh-960.avif?v=8da58efd 960w, /media/comun/follaje-bokeh-1280.avif?v=d203db79 1280w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/comun/follaje-bokeh-480.webp?v=ea7f3d20 480w, /media/comun/follaje-bokeh-720.webp?v=44cccb9b 720w, /media/comun/follaje-bokeh-960.webp?v=ca4391f9 960w, /media/comun/follaje-bokeh-1280.webp?v=190b6c2f 1280w' },
    ],
    widths: [480, 720, 960, 1280],
  },
  'mexico/bandera-recorte': {
    key: 'mexico/bandera-recorte',
    alt: 'Paño de la bandera de México ondeando: franjas verde, blanca y roja',
    origen: 'generado',
    seccion: 'Ciudad de México · hero, franja de paño anclada al borde superior',
    width: 938,
    height: 132,
    aspectRatio: 7.1061,
    transparente: true,
    src: '/media/mexico/bandera-recorte-938.png?v=76e3c913',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/mexico/bandera-recorte-480.png?v=5d7ace33 480w, /media/mexico/bandera-recorte-720.png?v=4ffc3dba 720w, /media/mexico/bandera-recorte-938.png?v=76e3c913 938w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/mexico/bandera-recorte-480.avif?v=a3aad4d7 480w, /media/mexico/bandera-recorte-720.avif?v=70fe7566 720w, /media/mexico/bandera-recorte-938.avif?v=4d503497 938w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/mexico/bandera-recorte-480.webp?v=48943857 480w, /media/mexico/bandera-recorte-720.webp?v=dc88c543 720w, /media/mexico/bandera-recorte-938.webp?v=5b1c7e29 938w' },
    ],
    widths: [480, 720, 938],
  },
  'cancun/cortina-recorte': {
    key: 'cancun/cortina-recorte',
    alt: 'Cortina blanca vaporosa con anilla dorada',
    origen: 'generado',
    seccion: 'Cancún VIP · hero, cortina que enmarca la escena por el borde izquierdo',
    width: 574,
    height: 1536,
    aspectRatio: 0.3737,
    transparente: true,
    src: '/media/cancun/cortina-recorte-574.png?v=3c08e9dc',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/cancun/cortina-recorte-480.png?v=a42e2f0e 480w, /media/cancun/cortina-recorte-574.png?v=3c08e9dc 574w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/cortina-recorte-480.avif?v=8cbf0684 480w, /media/cancun/cortina-recorte-574.avif?v=59682042 574w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/cortina-recorte-480.webp?v=04715888 480w, /media/cancun/cortina-recorte-574.webp?v=c1009f82 574w' },
    ],
    widths: [480, 574],
  },
  'cancun/palmera-recorte': {
    key: 'cancun/palmera-recorte',
    alt: 'Hoja de palmera tropical a contraluz',
    origen: 'generado',
    seccion: 'Cancún VIP · hero y lateral del programa · hoja de palmera en contraluz',
    width: 1013,
    height: 963,
    aspectRatio: 1.0519,
    transparente: true,
    src: '/media/cancun/palmera-recorte-1013.png?v=69e7c00e',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/cancun/palmera-recorte-480.png?v=f7175fac 480w, /media/cancun/palmera-recorte-720.png?v=8de7406b 720w, /media/cancun/palmera-recorte-960.png?v=04ad343e 960w, /media/cancun/palmera-recorte-1013.png?v=69e7c00e 1013w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/cancun/palmera-recorte-480.avif?v=c55deb98 480w, /media/cancun/palmera-recorte-720.avif?v=53ba8864 720w, /media/cancun/palmera-recorte-960.avif?v=a50feac4 960w, /media/cancun/palmera-recorte-1013.avif?v=c953a0cc 1013w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/cancun/palmera-recorte-480.webp?v=4a0c3fff 480w, /media/cancun/palmera-recorte-720.webp?v=529f66de 720w, /media/cancun/palmera-recorte-960.webp?v=c679cc2b 960w, /media/cancun/palmera-recorte-1013.webp?v=ca6c5a4a 1013w' },
    ],
    widths: [480, 720, 960, 1013],
  },
  'mexico/hojas-doradas-recorte': {
    key: 'mexico/hojas-doradas-recorte',
    alt: 'Ramas de laurel doradas y verde oliva',
    origen: 'generado',
    seccion: 'Ciudad de México · franja de entradas · ramas doradas en los márgenes',
    width: 706,
    height: 979,
    aspectRatio: 0.7211,
    transparente: true,
    src: '/media/mexico/hojas-doradas-recorte-706.png?v=9c2802bc',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/mexico/hojas-doradas-recorte-480.png?v=7ed40342 480w, /media/mexico/hojas-doradas-recorte-706.png?v=9c2802bc 706w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/mexico/hojas-doradas-recorte-480.avif?v=ce1289b0 480w, /media/mexico/hojas-doradas-recorte-706.avif?v=71289a67 706w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/mexico/hojas-doradas-recorte-480.webp?v=727d43ce 480w, /media/mexico/hojas-doradas-recorte-706.webp?v=25f1e365 706w' },
    ],
    widths: [480, 706],
  },
  'buenos-aires/bandera-seda-velo': {
    key: 'buenos-aires/bandera-seda-velo',
    alt: 'Velo de seda celeste y blanca ondeando, los colores de la bandera argentina',
    origen: 'generado',
    seccion: 'Buenos Aires · velo de seda celeste superponible (hero y banda de entradas)',
    width: 2048,
    height: 1003,
    aspectRatio: 2.0419,
    transparente: true,
    src: '/media/buenos-aires/bandera-seda-velo-2048.png?v=22869348',
    fallbackFormat: 'png',
    fallbackSrcSet: '/media/buenos-aires/bandera-seda-velo-640.png?v=4e8a515e 640w, /media/buenos-aires/bandera-seda-velo-960.png?v=708fc077 960w, /media/buenos-aires/bandera-seda-velo-1280.png?v=084a7715 1280w, /media/buenos-aires/bandera-seda-velo-1600.png?v=5ce140ec 1600w, /media/buenos-aires/bandera-seda-velo-2048.png?v=22869348 2048w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/buenos-aires/bandera-seda-velo-640.avif?v=1aab98e7 640w, /media/buenos-aires/bandera-seda-velo-960.avif?v=7218f39c 960w, /media/buenos-aires/bandera-seda-velo-1280.avif?v=a5b53477 1280w, /media/buenos-aires/bandera-seda-velo-1600.avif?v=ec7e6a5e 1600w, /media/buenos-aires/bandera-seda-velo-2048.avif?v=2b6d19f2 2048w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/buenos-aires/bandera-seda-velo-640.webp?v=c4ed013e 640w, /media/buenos-aires/bandera-seda-velo-960.webp?v=ab365107 960w, /media/buenos-aires/bandera-seda-velo-1280.webp?v=52206a7f 1280w, /media/buenos-aires/bandera-seda-velo-1600.webp?v=3b299b44 1600w, /media/buenos-aires/bandera-seda-velo-2048.webp?v=1415b63c 2048w' },
    ],
    widths: [640, 960, 1280, 1600, 2048],
  },
  'og/hub': {
    key: 'og/hub',
    alt: 'Gira 2027 · Nueva Humanidad Galáctica',
    origen: 'generado',
    seccion: 'Open Graph de la página · compuesto de hub/hero-cielo-galaxia',
    width: 1200,
    height: 630,
    aspectRatio: 1.9048,
    transparente: false,
    src: '/media/og/hub-1200.jpg?v=98e9bd85',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/og/hub-1200.jpg?v=98e9bd85 1200w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/og/hub-1200.avif?v=00e8c2a4 1200w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/og/hub-1200.webp?v=afaa2366 1200w' },
    ],
    widths: [1200],
  },
  'og/buenos-aires': {
    key: 'og/buenos-aires',
    alt: 'Buenos Aires · 6-7 de febrero de 2027',
    origen: 'generado',
    seccion: 'Open Graph de la página · compuesto de buenos-aires/hero',
    width: 1200,
    height: 630,
    aspectRatio: 1.9048,
    transparente: false,
    src: '/media/og/buenos-aires-1200.jpg?v=f8607e61',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/og/buenos-aires-1200.jpg?v=f8607e61 1200w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/og/buenos-aires-1200.avif?v=485db0eb 1200w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/og/buenos-aires-1200.webp?v=a7a11096 1200w' },
    ],
    widths: [1200],
  },
  'og/bogota': {
    key: 'og/bogota',
    alt: 'Bogotá · 13-14 de febrero de 2027',
    origen: 'generado',
    seccion: 'Open Graph de la página · compuesto de bogota/hero',
    width: 1200,
    height: 630,
    aspectRatio: 1.9048,
    transparente: false,
    src: '/media/og/bogota-1200.jpg?v=f953ec8e',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/og/bogota-1200.jpg?v=f953ec8e 1200w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/og/bogota-1200.avif?v=12806981 1200w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/og/bogota-1200.webp?v=e487c3ac 1200w' },
    ],
    widths: [1200],
  },
  'og/miami': {
    key: 'og/miami',
    alt: 'Miami · 6-7 de febrero de 2027',
    origen: 'generado',
    seccion: 'Open Graph de la página · compuesto de miami/hero',
    width: 1200,
    height: 630,
    aspectRatio: 1.9048,
    transparente: false,
    src: '/media/og/miami-1200.jpg?v=251633d9',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/og/miami-1200.jpg?v=251633d9 1200w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/og/miami-1200.avif?v=d1555446 1200w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/og/miami-1200.webp?v=4811fc57 1200w' },
    ],
    widths: [1200],
  },
  'og/mexico': {
    key: 'og/mexico',
    alt: 'Ciudad de México · 20-21 de febrero de 2027',
    origen: 'generado',
    seccion: 'Open Graph de la página · compuesto de mexico/hero',
    width: 1200,
    height: 630,
    aspectRatio: 1.9048,
    transparente: false,
    src: '/media/og/mexico-1200.jpg?v=3b873e16',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/og/mexico-1200.jpg?v=3b873e16 1200w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/og/mexico-1200.avif?v=01baa98a 1200w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/og/mexico-1200.webp?v=6f1b3d5f 1200w' },
    ],
    widths: [1200],
  },
  'og/cancun': {
    key: 'og/cancun',
    alt: 'Cancún / Riviera Maya VIP · 25-27 de febrero de 2027',
    origen: 'generado',
    seccion: 'Open Graph de la página · compuesto de cancun/hero',
    width: 1200,
    height: 630,
    aspectRatio: 1.9048,
    transparente: false,
    src: '/media/og/cancun-1200.jpg?v=fe51e2c0',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/og/cancun-1200.jpg?v=fe51e2c0 1200w',
    sources: [
      { format: 'avif', type: MIME.avif, srcSet: '/media/og/cancun-1200.avif?v=0c53ccc9 1200w' },
      { format: 'webp', type: MIME.webp, srcSet: '/media/og/cancun-1200.webp?v=031190e9 1200w' },
    ],
    widths: [1200],
  },
  /* ---------------------------------------------------------------------------
   * FUNES · las tres piezas salen del FLYER QUE MANDO EL CLIENTE (23-sep-2026),
   * no de gpt-image-2: es material suyo, y por eso `origen: 'real'`.
   * `derive.py` no las produjo porque entonces esta maquina no tenia codificador AVIF;
   * se sirven en WebP + JPEG, que es lo que hay, y la proxima pasada completa
   * del pipeline les anadira el AVIF sin tocar ninguna pagina.
   * ------------------------------------------------------------------------ */
  'funes/flyer': {
    key: 'funes/flyer',
    alt: 'Flyer de la convivencia «Encuentro con el Ser» en Alma Sana, Funes, 2-3 de febrero de 2027',
    origen: 'real',
    seccion: 'Funes · hero (el flyer del cliente, entero)',
    width: 1280,
    height: 720,
    aspectRatio: 1.7778,
    transparente: false,
    src: '/media/funes/flyer-1280.jpg?v=df30578f',
    fallbackFormat: 'jpg',
    fallbackSrcSet:
      '/media/funes/flyer-480.jpg?v=c38f3e03 480w, /media/funes/flyer-640.jpg?v=7eccf58f 640w, /media/funes/flyer-960.jpg?v=f5de3c39 960w, /media/funes/flyer-1280.jpg?v=df30578f 1280w',
    sources: [
      {
        format: 'webp',
        type: MIME.webp,
        srcSet:
          '/media/funes/flyer-480.webp?v=7d2f2685 480w, /media/funes/flyer-640.webp?v=1f01b6a6 640w, /media/funes/flyer-960.webp?v=34811bc0 960w, /media/funes/flyer-1280.webp?v=77a614b4 1280w',
      },
    ],
    widths: [480, 640, 960, 1280],
  },
  'hub/card-funes': {
    key: 'hub/card-funes',
    alt: 'Domo geodesico blanco entre arboles y jardin, en Alma Sana (Funes)',
    origen: 'real',
    seccion: 'Hub · Ruta del Despertar · tarjeta 1 (recorte del flyer, sin rotulos)',
    width: 480,
    height: 719,
    aspectRatio: 0.6676,
    transparente: false,
    src: '/media/hub/card-funes-480.jpg?v=70deedfb',
    fallbackFormat: 'jpg',
    fallbackSrcSet:
      '/media/hub/card-funes-320.jpg?v=b7f2bae6 320w, /media/hub/card-funes-480.jpg?v=70deedfb 480w',
    sources: [
      {
        format: 'webp',
        type: MIME.webp,
        srcSet:
          '/media/hub/card-funes-320.webp?v=bb33de39 320w, /media/hub/card-funes-480.webp?v=6608dc60 480w',
      },
    ],
    widths: [320, 480],
  },
  'og/funes': {
    key: 'og/funes',
    alt: 'Funes, Santa Fe · 2-3 de febrero de 2027',
    origen: 'real',
    seccion: 'Open Graph de la pagina · fotograma del video del cliente (2-nov-2026)',
    width: 1200,
    height: 630,
    aspectRatio: 1.9048,
    transparente: false,
    src: '/media/og/funes-1200.jpg?v=0c359cc5',
    fallbackFormat: 'jpg',
    fallbackSrcSet: '/media/og/funes-1200.jpg?v=0c359cc5 1200w',
    sources: [
      { format: 'webp', type: MIME.webp, srcSet: '/media/og/funes-1200.webp?v=06a85685 1200w' },
    ],
    widths: [1200],
  },
} as const satisfies Record<string, Asset>;

export type AssetKey = keyof typeof assets;

/** Acceso tipado: si la clave no existe, el error es de compilación, no de runtime. */
export function getAsset(key: AssetKey): Asset {
  return assets[key];
}

/** `srcSet` de un formato concreto, o `undefined` si ese asset no lo publica. */
export function srcSet(key: AssetKey, format: AssetFormato): string | undefined {
  return assets[key].sources.find((s) => s.format === format)?.srcSet;
}

export { MIME as ASSET_MIME };
